import clsx from "clsx";
import { ComponentPropsWithoutRef, ReactNode, forwardRef } from "react";
import { Link, type To } from "react-router";

type PillVariant = "green-outline" | "pink-outline" | "green-soft";
type PillSize = "xs" | "sm" | "md";

type BasePillButtonProps = {
  className?: string;
  variant?: PillVariant;
  size?: PillSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
};

type PillButtonAsButton = BasePillButtonProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof BasePillButtonProps> & {
    to?: undefined;
  };

type PillButtonAsLink = BasePillButtonProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, keyof BasePillButtonProps> & {
    to: To;
  };

export type PillButtonProps = PillButtonAsButton | PillButtonAsLink;

const variantClasses: Record<PillVariant, string> = {
  "green-outline": "border border-butter-green/80 bg-transparent",
  "pink-outline": "border border-butter-pink bg-white",
  "green-soft": "border border-transparent bg-butter-cream-light",
};

const sizeClasses: Record<PillSize, string> = {
  xs: "px-3 py-1",
  sm: "px-4 py-1.5",
  md: "px-4 py-2.5",
};

export const PillButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  PillButtonProps
>(function PillButton(
  {
    children,
    className,
    variant = "green-outline",
    size = "xs",
    leftIcon,
    rightIcon,
    ...props
  },
  ref,
) {
  const classes = clsx(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-xs font-bold text-butter-green transition-transform hover:scale-[1.03]",
    variantClasses[variant],
    sizeClasses[size],
    className,
  );

  const content = (
    <>
      {leftIcon}
      {children}
      {rightIcon}
    </>
  );

  if (props.to !== undefined) {
    const { to, ...linkProps } = props as PillButtonAsLink;
    return (
      <Link className={classes} to={to} ref={ref as never} {...linkProps}>
        {content}
      </Link>
    );
  }

  const buttonProps = props as PillButtonAsButton;
  return (
    <button
      className={classes}
      ref={ref as React.Ref<HTMLButtonElement>}
      {...buttonProps}
    >
      {content}
    </button>
  );
});
